// 004 (FR-026, FR-046; research R12; data-model §4): the selected child, in memory. The first
// linked child is selected; a selection is kept while that child is still linked; unlinked children
// are never selected. The selected child's school becomes the header school (appCurrentSchool).
// 005's student selector uses useSelectedStudent / selectStudent (routes.md seam).
import { useSyncExternalStore } from "react";

import type { Api } from "@/api/types";
import type { SchoolIdentity } from "@/session/types";

type Student = Api["Student"];

export function createSelection() {
  let selected: string | null = null;
  let last: readonly Student[] = [];
  let setSchool: ((s: SchoolIdentity | null) => void) | null = null;
  const listeners = new Set<() => void>();
  const linked = (list: readonly Student[]) =>
    list.filter((s) => s.linked && s.school !== null);

  function apply() {
    const candidates = linked(last);
    const keep = candidates.find((s) => s.id === selected);
    const next = keep ?? candidates[0] ?? null;
    const changed = (next?.id ?? null) !== selected;
    selected = next?.id ?? null;
    const school = next?.school ?? null;
    setSchool?.(
      school
        ? { id: school.id, name: school.name, logoUrl: school.logoUrl }
        : null,
    );
    if (changed) listeners.forEach((l) => l());
  }

  return {
    get: () => selected,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    /** Called whenever the children list changes (null = not loaded: nothing changes). */
    sync(
      list: readonly Student[] | null,
      set: (s: SchoolIdentity | null) => void,
    ) {
      setSchool = set;
      if (list === null) return;
      last = list;
      apply();
    },
    select(id: string) {
      // apply() compares with `selected`, which is already the new id here, so notify for it.
      const before = selected;
      selected = id;
      apply();
      if (selected !== before) listeners.forEach((l) => l());
    },
    clear() {
      selected = null;
      last = [];
      listeners.forEach((l) => l());
    },
  };
}

export const appSelection = createSelection();

export function selectStudent(id: string): void {
  appSelection.select(id);
}

export function useSelectedStudent(): string | null {
  return useSyncExternalStore(
    appSelection.subscribe,
    appSelection.get,
    appSelection.get,
  );
}
